import { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import "../css/Dashboard.css";
import "../css/Notifications.css";
import useIdleLogout from "../hooks/useIdleLogout";

/* ---------------- CSV Export helpers ---------------- */
function csvEscape(cell) {
  const s = String(cell ?? "");
  const needsQuote = /[",\n]/.test(s);
  const esc = s.replace(/"/g, '""');
  return needsQuote ? `"${esc}"` : esc;
}
function downloadCSV(filename, rows) {
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + rows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ---------------- ENV ---------------- */
const OFFERS_API = (process.env.REACT_APP_OFFERS_API_URL || "").replace(/\/+$/, "");

/* ---------------- helpers ---------------- */
const toLocal = (s) => {
  if (!s) return "-";
  return String(s).replace("T", " ").replace(".000Z", "");
};

async function fetchJson(url, opts = {}, timeoutMs = 15000) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...opts, signal: controller.signal });
    const text = await res.text();
    let json = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = null;
    }

    if (!res.ok) {
      const msg =
        json?.message ||
        json?.error ||
        json?.errors?.[0] ||
        `Request failed (HTTP ${res.status})`;
      throw new Error(msg);
    }

    return json ?? {};
  } finally {
    clearTimeout(t);
  }
}

function getAuthHeaders(withJson = false) {
  const t = (localStorage.getItem("access_token") || "").trim();
  const h = { Accept: "application/json" };
  if (withJson) h["Content-Type"] = "application/json";
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

const clampNum = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.max(min, Math.min(max, n));
};
const clampInt = (v, min, max) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const x = Math.trunc(n);
  return Math.max(min, Math.min(max, x));
};

export default function OffersAndRewards() {
  useIdleLogout();

  /* ---------------- FORM (CREATE) STATE ---------------- */
  const [title, setTitle] = useState("");
  const [sub, setSub] = useState("");
  const [icon, setIcon] = useState("");
  const [cta, setCta] = useState("");
  const [category, setCategory] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [startDate, setStartDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [active, setActive] = useState(true);
  const [forAll, setForAll] = useState(true);
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const [maxUsesPerUser, setMaxUsesPerUser] = useState("");
  const [maxUsesTotal, setMaxUsesTotal] = useState("");

  // Additional fields (kept)
  const [tint, setTint] = useState("#000000");
  const [applicableRideType, setApplicableRideType] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  /* ---------------- LIST STATE ---------------- */
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  /* ---------------- INLINE EDIT STATE ---------------- */
  const [rowEditId, setRowEditId] = useState(null);
  const [rowDraft, setRowDraft] = useState(null);
  const [rowSaving, setRowSaving] = useState(false);

  const wideEdge = { marginInline: "clamp(-16px, -2.5vw, -32px)" };
  const TOP_PAD = { paddingInline: "clamp(16px, 2.5vw, 32px)" };

  /* ---------------- MAP API RESPONSE (selected fields) ---------------- */
  const mapOffer = (r) => ({
    id: r?.id ?? null,
    title: r?.title ?? "",
    sub: r?.sub ?? "",
    icon: r?.icon ?? "",
    cta: r?.cta ?? "",
    tint: r?.tint ?? null,
    category: r?.category ?? "",
    promoCode: r?.promoCode ?? "",
    deepLink: r?.deepLink ?? "",
    startDate: r?.startDate ?? null,
    expiryDate: r?.expiryDate ?? null,
    active: Boolean(r?.active),
    for_all: Boolean(r?.for_all),
    max_uses_per_user: r?.max_uses_per_user ?? null,
    max_uses_total: r?.max_uses_total ?? null,
    discount_type: r?.discount_type ?? "percentage",
    discount_value: Number(r?.discount_value ?? 0),
    applicable_ride_type: r?.applicable_ride_type ?? null,
    createdAt: r?.createdAt ?? null,
    updatedAt: r?.updatedAt ?? null,
  });

  /* ---------------- LOAD LIST ---------------- */
  const loadOffers = useCallback(async () => {
    if (!OFFERS_API) {
      setError("Offers API is not configured. Set REACT_APP_OFFERS_API_URL.");
      setOffers([]);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const url = new URL(OFFERS_API, window.location.origin);

      const json = await fetchJson(url.toString(), {
        headers: getAuthHeaders(false),
      });

      const arr = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
      setOffers(arr.map(mapOffer));
    } catch (e) {
      setError(e.message || "Failed to load offers.");
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  // If the currently edited row is removed from the list (e.g., by delete), cancel editing
  useEffect(() => {
    if (rowEditId && !offers.some(o => o.id === rowEditId)) {
      setRowEditId(null);
      setRowDraft(null);
    }
  }, [offers, rowEditId]);

  /* ---------------- DATETIME CONVERT ---------------- */
  const toMysqlDatetime = (dtLocal) => {
    if (!dtLocal) return null;
    const s = String(dtLocal);
    if (!s.includes("T")) return s;
    const [d, t] = s.split("T");
    const hhmm = (t || "").slice(0, 5);
    return `${d} ${hhmm}:00`;
  };

  const mysqlToDtLocal = (v) => {
    if (!v) return "";
    const s = String(v).replace("T", " ");
    const [d, t] = s.split(" ");
    const hhmm = (t || "").slice(0, 5);
    return d && hhmm ? `${d}T${hhmm}` : "";
  };

  /* ---------------- INPUT HANDLERS (CLAMP) ---------------- */
  const handleDiscountValueChange = (val) => {
    if (val === "") return setDiscountValue("");
    const n = clampNum(val, 0, discountType === "percentage" ? 100 : 999999);
    if (n === null) return;
    setDiscountValue(String(n));
  };
  const handleDiscountValueBlur = () => {
    if (discountValue === "") return;
    const n = clampNum(discountValue, 0, discountType === "percentage" ? 100 : 999999);
    setDiscountValue(String(n ?? 0));
  };

  const handleMaxUsesPerUserChange = (val) => {
    if (val === "") return setMaxUsesPerUser("");
    const n = clampInt(val, 1, 999999);
    if (n === null) return;
    setMaxUsesPerUser(String(n));
  };
  const handleMaxUsesPerUserBlur = () => {
    if (maxUsesPerUser === "") return;
    const n = clampInt(maxUsesPerUser, 1, 999999);
    setMaxUsesPerUser(String(n ?? ""));
  };

  const handleMaxUsesTotalChange = (val) => {
    if (val === "") return setMaxUsesTotal("");
    const n = clampInt(val, 1, 999999);
    if (n === null) return;
    setMaxUsesTotal(String(n));
  };
  const handleMaxUsesTotalBlur = () => {
    if (maxUsesTotal === "") return;
    const n = clampInt(maxUsesTotal, 1, 999999);
    setMaxUsesTotal(String(n ?? ""));
  };

  /* ---------------- CREATE NEW ---------------- */
  const handleSubmitNew = async (e) => {
    e.preventDefault();

    if (!OFFERS_API) {
      Swal.fire({
        icon: "error",
        title: "Missing API",
        text: "Set REACT_APP_OFFERS_API_URL",
      });
      return;
    }

    if (!title.trim()) return Swal.fire({ icon: "error", title: "Title is required" });
    if (!startDate) return Swal.fire({ icon: "error", title: "Start Date is required" });
    if (!discountType) return Swal.fire({ icon: "error", title: "Discount type is required" });

    const dv = clampNum(discountValue, 0, discountType === "percentage" ? 100 : 999999);
    if (dv == null) return Swal.fire({ icon: "error", title: "Invalid discount value" });

    const mup = maxUsesPerUser ? clampInt(maxUsesPerUser, 1, 999999) : null;
    const mut = maxUsesTotal ? clampInt(maxUsesTotal, 1, 999999) : null;

    const payload = {
      title: title.trim(),
      sub: sub.trim() || null,
      icon: icon.trim() || null,
      cta: cta.trim() || null,
      category: category.trim() || null,
      promoCode: promoCode.trim() || null,
      deepLink: deepLink.trim() || null,
      startDate: toMysqlDatetime(startDate),
      expiryDate: expiryDate ? toMysqlDatetime(expiryDate) : null,
      active: active,
      for_all: forAll,
      discount_type: discountType,
      discount_value: dv,
      max_uses_per_user: mup,
      max_uses_total: mut,

      // Kept additional fields
      tint: tint || null,
      applicable_ride_type: applicableRideType.trim() || null,
      createdAt: createdAt ? toMysqlDatetime(createdAt) : null,
      updatedAt: updatedAt ? toMysqlDatetime(updatedAt) : null,
    };

    try {
      setSavingNew(true);

      await fetchJson(OFFERS_API, {
        method: "POST",
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      Swal.fire({
        icon: "success",
        title: "Offer created",
        timer: 1200,
        showConfirmButton: false,
      });

      // Reset all form fields
      setTitle("");
      setSub("");
      setIcon("");
      setCta("");
      setCategory("");
      setPromoCode("");
      setDeepLink("");
      setStartDate("");
      setExpiryDate("");
      setActive(true);
      setForAll(true);
      setDiscountType("percentage");
      setDiscountValue("10");
      setMaxUsesPerUser("");
      setMaxUsesTotal("");

      setTint("#000000");
      setApplicableRideType("");
      setCreatedAt("");
      setUpdatedAt("");

      await loadOffers();
    } catch (e2) {
      Swal.fire({
        icon: "error",
        title: "Save failed",
        text: e2.message || "Failed to save offer.",
      });
    } finally {
      setSavingNew(false);
    }
  };

  /* ---------------- INLINE EDIT ---------------- */
  const beginRowEdit = (r) => {
    setRowEditId(r.id);
    setRowDraft({
      title: r.title ?? "",
      sub: r.sub ?? "",
      icon: r.icon ?? "",
      cta: r.cta ?? "",
      tint: r.tint ?? "#000000",
      category: r.category ?? "",
      promoCode: r.promoCode ?? "",
      deepLink: r.deepLink ?? "",
      startDate: mysqlToDtLocal(r.startDate),
      expiryDate: mysqlToDtLocal(r.expiryDate),
      active: r.active,
      for_all: r.for_all,
      discount_type: r.discount_type ?? "percentage",
      discount_value: String(r.discount_value ?? "0"),
      max_uses_per_user: r.max_uses_per_user != null ? String(r.max_uses_per_user) : "",
      max_uses_total: r.max_uses_total != null ? String(r.max_uses_total) : "",
      applicable_ride_type: r.applicable_ride_type ?? "",
      createdAt: mysqlToDtLocal(r.createdAt),
      updatedAt: mysqlToDtLocal(r.updatedAt),
    });
  };

  const cancelRowEdit = () => {
    setRowEditId(null);
    setRowDraft(null);
  };

  const updateDraft = (key, val) => {
    setRowDraft((prev) => ({ ...(prev || {}), [key]: val }));
  };

  // Discount value handler with type awareness
  const updateDraftDiscountValue = (val) => {
    if (val === "") return updateDraft("discount_value", "");
    const type = rowDraft?.discount_type ?? "percentage";
    const n = clampNum(val, 0, type === "percentage" ? 100 : 999999);
    if (n === null) return;
    updateDraft("discount_value", String(n));
  };
  const blurDraftDiscountValue = () => {
    const cur = rowDraft?.discount_value ?? "";
    if (cur === "") return;
    const type = rowDraft?.discount_type ?? "percentage";
    const n = clampNum(cur, 0, type === "percentage" ? 100 : 999999);
    updateDraft("discount_value", String(n ?? 0));
  };

  const updateDraftMaxUsesPerUser = (val) => {
    if (val === "") return updateDraft("max_uses_per_user", "");
    const n = clampInt(val, 1, 999999);
    if (n === null) return;
    updateDraft("max_uses_per_user", String(n));
  };
  const blurDraftMaxUsesPerUser = () => {
    const cur = rowDraft?.max_uses_per_user ?? "";
    if (cur === "") return;
    const n = clampInt(cur, 1, 999999);
    updateDraft("max_uses_per_user", String(n ?? ""));
  };

  const updateDraftMaxUsesTotal = (val) => {
    if (val === "") return updateDraft("max_uses_total", "");
    const n = clampInt(val, 1, 999999);
    if (n === null) return;
    updateDraft("max_uses_total", String(n));
  };
  const blurDraftMaxUsesTotal = () => {
    const cur = rowDraft?.max_uses_total ?? "";
    if (cur === "") return;
    const n = clampInt(cur, 1, 999999);
    updateDraft("max_uses_total", String(n ?? ""));
  };

  const saveRowEdit = async () => {
    if (!rowEditId || !rowDraft) return;
    if (!OFFERS_API) return;

    const titleVal = String(rowDraft.title || "").trim();
    if (!titleVal) return Swal.fire({ icon: "error", title: "Title is required" });
    if (!rowDraft.startDate) return Swal.fire({ icon: "error", title: "Start Date is required" });

    const type = rowDraft.discount_type;
    const dv = clampNum(rowDraft.discount_value, 0, type === "percentage" ? 100 : 999999);
    if (dv == null) return Swal.fire({ icon: "error", title: "Invalid discount value" });

    const mup = rowDraft.max_uses_per_user ? clampInt(rowDraft.max_uses_per_user, 1, 999999) : null;
    const mut = rowDraft.max_uses_total ? clampInt(rowDraft.max_uses_total, 1, 999999) : null;

    const payload = {
      title: titleVal,
      sub: String(rowDraft.sub || "").trim() || null,
      icon: String(rowDraft.icon || "").trim() || null,
      cta: String(rowDraft.cta || "").trim() || null,
      category: String(rowDraft.category || "").trim() || null,
      promoCode: String(rowDraft.promoCode || "").trim() || null,
      deepLink: String(rowDraft.deepLink || "").trim() || null,
      startDate: toMysqlDatetime(rowDraft.startDate),
      expiryDate: rowDraft.expiryDate ? toMysqlDatetime(rowDraft.expiryDate) : null,
      active: Boolean(rowDraft.active),
      for_all: Boolean(rowDraft.for_all),
      discount_type: type,
      discount_value: dv,
      max_uses_per_user: mup,
      max_uses_total: mut,

      // Kept additional fields
      tint: rowDraft.tint || null,
      applicable_ride_type: String(rowDraft.applicable_ride_type || "").trim() || null,
      createdAt: rowDraft.createdAt ? toMysqlDatetime(rowDraft.createdAt) : null,
      updatedAt: rowDraft.updatedAt ? toMysqlDatetime(rowDraft.updatedAt) : null,
    };

    try {
      setRowSaving(true);

      await fetchJson(`${OFFERS_API}/${encodeURIComponent(rowEditId)}`, {
        method: "PUT",
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });

      Swal.fire({
        icon: "success",
        title: "Changes saved",
        timer: 1100,
        showConfirmButton: false,
      });

      cancelRowEdit();
      await loadOffers();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: e.message || "Failed to update.",
      });
    } finally {
      setRowSaving(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = async (id) => {
    if (!OFFERS_API) return;

    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete this offer?",
      text: `Offer ID: ${id}`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!ok.isConfirmed) return;

    try {
      setDeletingId(id);

      await fetchJson(`${OFFERS_API}/${encodeURIComponent(id)}`, {
        method: "DELETE",
        headers: getAuthHeaders(false),
      });

      Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 1000,
        showConfirmButton: false,
      });

      if (rowEditId === id) cancelRowEdit();
      
      // 🔁 Reload the list to reflect actual server state
      await loadOffers();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: e.message || "Failed to delete.",
      });
      await loadOffers();
    } finally {
      setDeletingId(null);
    }
  };

  /* ---------------- EXPORT ---------------- */
  const exportAllToExcel = () => {
    const now = new Date();
    const ts = now.toISOString().replace(/[:.-]/g, "").slice(0, 15);
    const fname = `offers_${ts}.csv`;

    const headerLines = [
      ["Report Title", "Offers"],
      ["Exported At", now.toLocaleString()],
      [],
    ].map((row) => row.map(csvEscape).join(","));

    const tableHeader = [
      "SI.No",
      "ID",
      "Title",
      "Subtitle",
      "Icon",
      "CTA",
      "Tint",
      "Category",
      "Promo Code",
      "Deep Link",
      "Start Date",
      "Expiry Date",
      "Active",
      "For All",
      "Max Uses/User",
      "Max Uses Total",
      "Discount Type",
      "Discount Value",
      "Applicable Ride Type",
      "Created At",
      "Updated At",
    ]
      .map(csvEscape)
      .join(",");

    const rows = offers.map((r, idx) => {
      return [
        String(idx + 1),
        r.id,
        r.title,
        r.sub || "",
        r.icon || "",
        r.cta || "",
        r.tint || "",
        r.category || "",
        r.promoCode || "",
        r.deepLink || "",
        r.startDate ?? "",
        r.expiryDate ?? "",
        r.active ? "Active" : "Inactive",
        r.for_all ? "Yes" : "No",
        r.max_uses_per_user ?? "",
        r.max_uses_total ?? "",
        r.discount_type === "percentage" ? "%" : "Fixed",
        r.discount_value,
        r.applicable_ride_type || "",
        r.createdAt ?? "",
        r.updatedAt ?? "",
      ]
        .map(csvEscape)
        .join(",");
    });

    downloadCSV(fname, [...headerLines, tableHeader, ...rows]);
  };

  /* ---------------- EXPORT BUTTON STYLE ---------------- */
  const EXPORT_GREEN = "#1e9e56";
  const exportBtnBase = {
    border: `2px solid ${EXPORT_GREEN}`,
    color: EXPORT_GREEN,
    background: "transparent",
    borderRadius: 999,
    fontWeight: 600,
    transition: "all 0.25s ease",
    padding: "6px 18px",
    letterSpacing: "0.3px",
    boxShadow: "none",
  };
  const exportBtnHover = {
    background: EXPORT_GREEN,
    color: "#ffffff",
    boxShadow: "none",
    transform: "translateY(-1px)",
  };
  const exportBtnDisabled = {
    opacity: 0.5,
    cursor: "not-allowed",
    boxShadow: "none",
    transform: "none",
  };
  const [isExportHover, setIsExportHover] = useState(false);
  const exportBtnStyle = {
    ...exportBtnBase,
    ...(isExportHover ? exportBtnHover : null),
    ...(!offers.length ? exportBtnDisabled : null),
  };

  return (
    <div className="p-4 flex-grow-1">
      <style>{`
        .offers-table th { white-space: nowrap; }
        .offers-table { table-layout: fixed; }
        .offers-table td, .offers-table th { vertical-align: middle; }

        .offer-inline-input,
        .offer-inline-select,
        .offer-inline-textarea {
          width: 100% !important;
          height: 32px !important;
          padding: 4px 10px !important;
          border-radius: 12px !important;
          box-shadow: none !important;
          min-width: 0 !important;
        }
        .offer-inline-textarea {
          height: 60px !important;
          resize: vertical;
        }

        .offer-edit-cell { overflow: hidden; }

        .offer-actions .btn,
        .offer-actions button { box-shadow: none !important; }
        .offer-actions { display: inline-flex; gap: 8px; align-items: center; flex-wrap: nowrap; }
        .offer-actions .btn {
          border-radius: 12px !important;
          padding: 6px 12px !important;
          font-weight: 700 !important;
          min-width: 76px;
        }
        
        .col-sl { width: 70px; }
        .col-id { width: 90px; }
        .col-title { width: 150px; }
        .col-sub { width: 200px; }
        .col-icon { width: 100px; }
        .col-cta { width: 150px; }
        .col-tint { width: 100px; }
        .col-category { width: 120px; }
        .col-promo { width: 130px; }
        .col-deepLink { width: 120px; }
        .col-start { width: 160px; }
        .col-expiry { width: 160px; }
        .col-active { width: 100px; }
        .col-forAll { width: 80px; }
        .col-maxPerUser { width: 120px; }
        .col-maxTotal { width: 120px; }
        .col-discount-type { width: 100px; }
        .col-discount-value { width: 100px; }
        .col-applicable_ride_type { width: 170px; }
        .col-createdAt { width: 160px; }
        .col-updatedAt { width: 160px; }
        .col-actions { width: 200px; }

        /* Tint input enhancement */
        input[type="color"].form-control {
          padding: 2px 4px;
          height: 38px;
          cursor: pointer;
          border-radius: 8px;
          border: 1px solid #ced4da;
          background: #fff;
        }
        input[type="color"].form-control::-webkit-color-swatch-wrapper {
          padding: 0;
        }
        input[type="color"].form-control::-webkit-color-swatch {
          border: none;
          border-radius: 6px;
        }

        .checkbox-group {
          display: flex;
          gap: 2rem;
          align-items: center;
          flex-wrap: wrap;
          background: #f8f9fa;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          margin-top: 0.5rem;
        }

        .checkbox-group .form-check {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin: 0;
          min-width: 120px;
        }

        .checkbox-group .form-check-input {
          width: 2.5rem;
          height: 1.25rem;
          margin: 0;
          background-color: #e9ecef;
          border-color: #ced4da;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
          background-position: left center;
          border-radius: 2rem;
          transition: background-position 0.15s ease-in-out;
          appearance: none;
          cursor: pointer;
        }

        .checkbox-group .form-check-input:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
          background-position: right center;
        }

        .checkbox-group .form-check-label {
          font-weight: 500;
          color: #2c3e50;
          cursor: pointer;
          user-select: none;
        }

        .date-group {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .date-field {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 0.75rem 1rem;
          transition: box-shadow 0.2s;
        }

        .date-field:focus-within {
          box-shadow: 0 0 0 3px rgba(13,110,253,0.25);
        }

        .date-field label {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #6c757d;
          margin-bottom: 0.25rem;
        }

        .date-field input {
          width: 100%;
          border: none;
          background: transparent;
          padding: 0.25rem 0;
          font-size: 1rem;
          color: #2c3e50;
          outline: none;
        }

        .date-field input::-webkit-calendar-picker-indicator {
          background: transparent;
          bottom: 0;
          color: transparent;
          cursor: pointer;
          height: auto;
          left: 0;
          position: absolute;
          right: 0;
          top: 0;
          width: auto;
          opacity: 0;
        }

        .date-field .input-hint {
          font-size: 0.75rem;
          color: #adb5bd;
          margin-top: 0.25rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .date-field .input-hint svg {
          width: 14px;
          height: 14px;
          fill: currentColor;
        }

        @media (max-width: 768px) {
          .date-group {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          .checkbox-group {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }
        }
      `}</style>

      <div className="card p-4 system-card">
        <h4 className="section-title">Offers Management</h4>
        <p className="text-muted mb-4">
          Create and manage discount offers.
        </p>

        <form onSubmit={handleSubmitNew} className="settings-form">
          <div className="row g-3">
            {/* Core fields */}
            <div className="col-md-6">
              <label className="form-label">Title *</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Subtitle</label>
              <input
                type="text"
                className="form-control"
                value={sub}
                onChange={(e) => setSub(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Icon</label>
              <input
                type="text"
                className="form-control"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="e.g., car, gift"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">CTA</label>
              <input
                type="text"
                className="form-control"
                value={cta}
                onChange={(e) => setCta(e.target.value)}
                placeholder="Use code SAVE20"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Category</label>
              <input
                type="text"
                className="form-control"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="for_you, popular, etc."
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Promo Code</label>
              <input
                type="text"
                className="form-control"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Deep Link</label>
              <input
                type="text"
                className="form-control"
                value={deepLink}
                onChange={(e) => setDeepLink(e.target.value)}
                placeholder="OfferDetail"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Discount Type</label>
              <select
                className="form-select"
                value={discountType}
                onChange={(e) => {
                  setDiscountType(e.target.value);
                  setDiscountValue("");
                }}
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Discount Value</label>
              <input
                type="number"
                className="form-control"
                value={discountValue}
                onChange={(e) => handleDiscountValueChange(e.target.value)}
                onBlur={handleDiscountValueBlur}
                min="0"
                max={discountType === "percentage" ? 100 : undefined}
                step={discountType === "percentage" ? "0.1" : "1"}
                inputMode="decimal"
                required
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Max Uses Per User</label>
              <input
                type="number"
                className="form-control"
                value={maxUsesPerUser}
                onChange={(e) => handleMaxUsesPerUserChange(e.target.value)}
                onBlur={handleMaxUsesPerUserBlur}
                min="1"
                step="1"
                inputMode="numeric"
                placeholder="Unlimited"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Max Uses Total</label>
              <input
                type="number"
                className="form-control"
                value={maxUsesTotal}
                onChange={(e) => handleMaxUsesTotalChange(e.target.value)}
                onBlur={handleMaxUsesTotalBlur}
                min="1"
                step="1"
                inputMode="numeric"
                placeholder="Unlimited"
              />
            </div>

            <div className="col-md-3 d-flex align-items-center mt-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="offer-active"
                  className="form-check-input"
                  checked={active}
                  onChange={() => setActive((v) => !v)}
                />
                <label className="form-check-label" htmlFor="offer-active">Active</label>
              </div>
            </div>

            <div className="col-md-3 d-flex align-items-center mt-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="offer-forAll"
                  className="form-check-input"
                  checked={forAll}
                  onChange={() => setForAll((v) => !v)}
                />
                <label className="form-check-label" htmlFor="offer-forAll">For All Users</label>
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Start Date *</label>
              <input
                type="datetime-local"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Expiry Date (optional)</label>
              <input
                type="datetime-local"
                className="form-control"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>

            {/* Additional fields (kept) */}
            <div className="col-md-6">
              <label className="form-label">Tint (color)</label>
              <input
                type="color"
                className="form-control"
                value={tint}
                onChange={(e) => setTint(e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Applicable Ride Type</label>
              <input
                type="text"
                className="form-control"
                value={applicableRideType}
                onChange={(e) => setApplicableRideType(e.target.value)}
                placeholder="e.g., premium"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Created At</label>
              <input
                type="datetime-local"
                className="form-control"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Updated At</label>
              <input
                type="datetime-local"
                className="form-control"
                value={updatedAt}
                onChange={(e) => setUpdatedAt(e.target.value)}
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary w-100 mt-4" disabled={savingNew}>
            {savingNew ? "Saving…" : "Create Offer"}
          </button>
        </form>

        {/* ---------------- TABLE (LIST) ---------------- */}
        <div className="mt-4" style={wideEdge}>
          <div className="d-flex align-items-center justify-content-between mb-2" style={TOP_PAD}>
            <h5 className="mb-0">Existing Offers</h5>

            <button
              type="button"
              className="btn btn-outline-success btn-sm px-3 export-btn"
              onClick={exportAllToExcel}
              disabled={!offers.length}
              style={exportBtnStyle}
              onMouseEnter={() => setIsExportHover(true)}
              onMouseLeave={() => setIsExportHover(false)}
              onFocus={() => setIsExportHover(true)}
              onBlur={() => setIsExportHover(false)}
            >
              Export Excel
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mt-2 mb-3" style={TOP_PAD}>
              <small>{error}</small>
            </div>
          )}

          <div className="table-responsive contrast-surface" style={TOP_PAD}>
            <table className="table table-sm align-middle contrast-table w-100 offers-table">
              <thead>
                <tr>
                  <th className="col-sl">Sl.No</th>
                  <th className="col-id">ID</th>
                  <th className="col-title">Title</th>
                  <th className="col-sub">Subtitle</th>
                  <th className="col-icon">Icon</th>
                  <th className="col-cta">CTA</th>
                  <th className="col-tint">Tint</th>
                  <th className="col-category">Category</th>
                  <th className="col-promo">Promo Code</th>
                  <th className="col-deepLink">Deep Link</th>
                  <th className="col-start">Start</th>
                  <th className="col-expiry">Expiry</th>
                  <th className="col-active">Active</th>
                  <th className="col-forAll">For All</th>
                  <th className="col-maxPerUser">Max/User</th>
                  <th className="col-maxTotal">Max Total</th>
                  <th className="col-discount-type">Disc Type</th>
                  <th className="col-discount-value">Disc Value</th>
                  <th className="col-applicable_ride_type">Applicable Ride Type</th>
                  <th className="col-createdAt">Created At</th>
                  <th className="col-updatedAt">Updated At</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={22} className="text-muted text-center py-3">Loading…</td>
                  </tr>
                ) : offers.length === 0 ? (
                  <tr>
                    <td colSpan={22} className="text-muted text-center py-3">No offers found.</td>
                  </tr>
                ) : (
                  offers.map((r, idx) => {
                    const isRowEditing = rowEditId === r.id;
                    const busyDelete = deletingId === r.id;
                    const d = rowDraft || {};

                    return (
                      <tr key={r.id ?? idx}>
                        <td>{idx + 1}</td>
                        <td className="fw-semibold">{r.id}</td>

                        {/* Title */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              className="form-control form-control-sm offer-inline-input"
                              value={d.title ?? ""}
                              onChange={(e) => updateDraft("title", e.target.value)}
                            />
                          ) : (
                            r.title
                          )}
                        </td>

                        {/* Subtitle */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <textarea
                              className="form-control form-control-sm offer-inline-textarea"
                              value={d.sub ?? ""}
                              onChange={(e) => updateDraft("sub", e.target.value)}
                              rows="2"
                            />
                          ) : (
                            r.sub || "-"
                          )}
                        </td>

                        {/* Icon */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              className="form-control form-control-sm offer-inline-input"
                              value={d.icon ?? ""}
                              onChange={(e) => updateDraft("icon", e.target.value)}
                            />
                          ) : (
                            r.icon || "-"
                          )}
                        </td>

                        {/* CTA */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              className="form-control form-control-sm offer-inline-input"
                              value={d.cta ?? ""}
                              onChange={(e) => updateDraft("cta", e.target.value)}
                            />
                          ) : (
                            r.cta || "-"
                          )}
                        </td>

                        {/* Tint - enhanced color picker */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="color"
                              className="form-control form-control-sm offer-inline-input"
                              value={d.tint ?? "#000000"}
                              onChange={(e) => updateDraft("tint", e.target.value)}
                              style={{ padding: "2px", height: "32px" }}
                            />
                          ) : (
                            <span style={{ display: "inline-block", width: "20px", height: "20px", backgroundColor: r.tint, border: "1px solid #ccc", borderRadius: "4px" }} title={r.tint} />
                          )}
                        </td>

                        {/* Category */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              className="form-control form-control-sm offer-inline-input"
                              value={d.category ?? ""}
                              onChange={(e) => updateDraft("category", e.target.value)}
                            />
                          ) : (
                            r.category || "-"
                          )}
                        </td>

                        {/* Promo Code */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              className="form-control form-control-sm offer-inline-input"
                              value={d.promoCode ?? ""}
                              onChange={(e) => updateDraft("promoCode", e.target.value)}
                            />
                          ) : (
                            r.promoCode || "-"
                          )}
                        </td>

                        {/* Deep Link */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              className="form-control form-control-sm offer-inline-input"
                              value={d.deepLink ?? ""}
                              onChange={(e) => updateDraft("deepLink", e.target.value)}
                            />
                          ) : (
                            r.deepLink || "-"
                          )}
                        </td>

                        {/* Start Date */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="datetime-local"
                              className="form-control form-control-sm offer-inline-input"
                              value={d.startDate ?? ""}
                              onChange={(e) => updateDraft("startDate", e.target.value)}
                            />
                          ) : (
                            toLocal(r.startDate)
                          )}
                        </td>

                        {/* Expiry Date */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="datetime-local"
                              className="form-control form-control-sm offer-inline-input"
                              value={d.expiryDate ?? ""}
                              onChange={(e) => updateDraft("expiryDate", e.target.value)}
                            />
                          ) : (
                            toLocal(r.expiryDate)
                          )}
                        </td>

                        {/* Active */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <select
                              className="form-select form-select-sm offer-inline-select"
                              value={d.active ? "true" : "false"}
                              onChange={(e) => updateDraft("active", e.target.value === "true")}
                            >
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </select>
                          ) : r.active ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-secondary">Inactive</span>
                          )}
                        </td>

                        {/* For All */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <select
                              className="form-select form-select-sm offer-inline-select"
                              value={d.for_all ? "true" : "false"}
                              onChange={(e) => updateDraft("for_all", e.target.value === "true")}
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          ) : r.for_all ? (
                            <span className="badge bg-info">Yes</span>
                          ) : (
                            <span className="badge bg-secondary">No</span>
                          )}
                        </td>

                        {/* Max Uses Per User */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm offer-inline-input"
                              value={d.max_uses_per_user ?? ""}
                              onChange={(e) => updateDraftMaxUsesPerUser(e.target.value)}
                              onBlur={blurDraftMaxUsesPerUser}
                              min="1"
                              step="1"
                              inputMode="numeric"
                              placeholder="Unlimited"
                            />
                          ) : (
                            r.max_uses_per_user ?? "-"
                          )}
                        </td>

                        {/* Max Uses Total */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm offer-inline-input"
                              value={d.max_uses_total ?? ""}
                              onChange={(e) => updateDraftMaxUsesTotal(e.target.value)}
                              onBlur={blurDraftMaxUsesTotal}
                              min="1"
                              step="1"
                              inputMode="numeric"
                              placeholder="Unlimited"
                            />
                          ) : (
                            r.max_uses_total ?? "-"
                          )}
                        </td>

                        {/* Discount Type */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <select
                              className="form-select form-select-sm offer-inline-select"
                              value={d.discount_type ?? "percentage"}
                              onChange={(e) => updateDraft("discount_type", e.target.value)}
                            >
                              <option value="percentage">%</option>
                              <option value="fixed">Fixed</option>
                            </select>
                          ) : (
                            r.discount_type === "percentage" ? "%" : "Fixed"
                          )}
                        </td>

                        {/* Discount Value */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="number"
                              className="form-control form-control-sm offer-inline-input"
                              value={d.discount_value ?? "0"}
                              onChange={(e) => updateDraftDiscountValue(e.target.value)}
                              onBlur={blurDraftDiscountValue}
                              min="0"
                              max={d.discount_type === "percentage" ? 100 : undefined}
                              step={d.discount_type === "percentage" ? "0.1" : "1"}
                              inputMode="decimal"
                            />
                          ) : (
                            r.discount_value
                          )}
                        </td>

                        {/* Applicable Ride Type */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              className="form-control form-control-sm offer-inline-input"
                              value={d.applicable_ride_type ?? ""}
                              onChange={(e) => updateDraft("applicable_ride_type", e.target.value)}
                            />
                          ) : (
                            r.applicable_ride_type || "-"
                          )}
                        </td>

                        {/* Created At */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="datetime-local"
                              className="form-control form-control-sm offer-inline-input"
                              value={d.createdAt ?? ""}
                              onChange={(e) => updateDraft("createdAt", e.target.value)}
                            />
                          ) : (
                            toLocal(r.createdAt)
                          )}
                        </td>

                        {/* Updated At */}
                        <td className="offer-edit-cell">
                          {isRowEditing ? (
                            <input
                              type="datetime-local"
                              className="form-control form-control-sm offer-inline-input"
                              value={d.updatedAt ?? ""}
                              onChange={(e) => updateDraft("updatedAt", e.target.value)}
                            />
                          ) : (
                            toLocal(r.updatedAt)
                          )}
                        </td>

                        {/* Actions */}
                        <td className="offer-edit-cell">
                          <div className="offer-actions">
                            {isRowEditing ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={saveRowEdit}
                                  disabled={rowSaving}
                                >
                                  {rowSaving ? "Saving..." : "Save"}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={cancelRowEdit}
                                  disabled={rowSaving}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => beginRowEdit(r)}
                                  disabled={rowEditId && rowEditId !== r.id}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(r.id)}
                                  disabled={busyDelete || (rowEditId && rowEditId !== r.id)}
                                >
                                  {busyDelete ? "..." : "Delete"}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer className="dashboard-footer mt-4">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}